/**
 * 26 TAC §558.295 discharge notice (Phase 16E, DSC-19): client-side check that mirrors the server.
 * Hand-delivered or electronic notice at least 5 calendar days before discharge; a mailed notice at
 * least 8 working days before plus phone or in-person contact at least 5 days before. The server
 * also skips federal and agency holidays for mailed notices and is the final word.
 */
import type {
  DischargeNotice,
  DischargeNoticeException,
  DischargeNoticeMethod,
  DischargeNoticeRecipient,
  PhysicianNoticeMethod,
} from '../dtos/patientmr/dischargeSummary.dto';

export const NOTICE_METHOD_OPTIONS: { value: DischargeNoticeMethod; label: string }[] = [
  { value: 'HAND', label: 'Hand-delivered' },
  { value: 'MAIL', label: 'Mailed' },
  { value: 'ELECTRONIC', label: 'Electronic (secure)' },
];

export const NOTICE_RECIPIENT_OPTIONS: { value: DischargeNoticeRecipient; label: string }[] = [
  { value: 'LAR', label: 'Parent / legal representative' },
  { value: 'PATIENT', label: 'Patient' },
];

export const PHYSICIAN_NOTICE_OPTIONS: { value: PhysicianNoticeMethod; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'FAX', label: 'Fax' },
  { value: 'MAIL', label: 'Mail' },
  { value: 'ELECTRONIC', label: 'Electronic (secure)' },
  { value: 'IN_PERSON', label: 'In person' },
];

export const NOTICE_EXCEPTION_OPTIONS: { value: DischargeNoticeException; label: string }[] = [
  { value: 'CLIENT_REQUEST', label: 'Patient or family asked for the discharge' },
  { value: 'MEDICAL_EMERGENCY', label: 'Medical needs require it (emergency)' },
  { value: 'DISASTER', label: 'Disaster puts health or safety at risk' },
  { value: 'SAFETY_RISK', label: 'Staff or patient safety at risk' },
  { value: 'PHYSICIAN_ORDER', label: 'Physician ordered it' },
  { value: 'NON_PAYMENT', label: 'Non-payment for services' },
];

export const NOTICE_MIN_DAYS = 5;
export const MAILED_NOTICE_MIN_WORKING_DAYS = 8;

const isIsoDate = (v: string | null | undefined): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Mon–Fri days after `from` up to and including `to` (holidays not known here). */
export function workingDaysBetween(from: string, to: string): number {
  let count = 0;
  for (let day = addDays(from, 1); day <= to; day = addDays(day, 1)) {
    const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return count;
}

/** True when the advance period is met; null while dates are missing. */
export function noticePeriodMet(notice: Partial<DischargeNotice>, dischargeDate: string): boolean | null {
  if (!isIsoDate(notice.noticeDate) || !isIsoDate(dischargeDate) || !notice.noticeMethod) return null;
  if (notice.noticeMethod === 'MAIL') {
    const contactOk = isIsoDate(notice.personalContactDate) && addDays(notice.personalContactDate, NOTICE_MIN_DAYS) <= dischargeDate;
    return workingDaysBetween(notice.noticeDate, dischargeDate) >= MAILED_NOTICE_MIN_WORKING_DAYS && contactOk;
  }
  return addDays(notice.noticeDate, NOTICE_MIN_DAYS) <= dischargeDate;
}

/** Field names that still need a value before the discharge can be saved. */
export function missingNoticeFields(notice: Partial<DischargeNotice>, dischargeDate: string): string[] {
  const missing: string[] = [];
  if (!isIsoDate(notice.noticeDate)) missing.push('noticeDate');
  else if (isIsoDate(dischargeDate) && notice.noticeDate > dischargeDate) missing.push('noticeDate');
  if (!notice.noticeMethod) missing.push('noticeMethod');
  if (!notice.noticeRecipientType) missing.push('noticeRecipientType');
  if (!isIsoDate(notice.physicianNotifiedDate)) missing.push('physicianNotifiedDate');
  if (!notice.physicianNotifiedMethod) missing.push('physicianNotifiedMethod');
  if (noticePeriodMet(notice, dischargeDate) === false && !notice.noticeException) missing.push('noticeException');
  return missing;
}
